import { HttpClient } from '@angular/common/http';
import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DataTableDirective } from 'angular-datatables';
import {
    Observable,
    Subject,
    catchError,
    debounceTime,
    distinctUntilChanged,
    finalize,
    map,
    of,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import Swal from 'sweetalert2';
import { helper } from 'src/environments/helper';
import { AddBorderouComponent } from './add-borderou/add-borderou.component';
import { ViewBorderouComponent } from './view-borderou/view-borderou.component';
import { FormControl, FormGroup } from '@angular/forms';
import { BorderouService } from './borderou.service';
import { CommonService } from 'src/app/services/common.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { Generic, GenericCompartimentSearch } from 'src/app/interfaces/generic.interface';
import { UserOrganigramaSearchPayload } from 'src/app/interfaces/common.interface';
import { formatDate } from '@angular/common';
import { OperatoriService } from 'src/app/platform/nomenclatoare/operatori/operatori.service';
import { OperatorAccess } from 'src/app/platform/nomenclatoare/operatori/operatori.interface';
import { AppFilesService } from 'src/app/services/app-files.service';

@Component({
    selector: 'app-borderou',
    templateUrl: './borderou.component.html',
    styleUrls: ['./borderou.component.scss'],
})
export class BorderouComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    helper = helper;
    isLoading = false;
    isFilterLoading = false;
    showCard = false;
    today = new Date();

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;

    private destroy$ = new Subject<void>();

    userAccesses: OperatorAccess[] = [];
    activeAccess: OperatorAccess | null = null;

    directiiOrigin: Generic[] = [];
    filteredDirectiiOrigin: Generic[] = [];
    directiiOriginControl = new FormControl();

    compartimenteOrigin: GenericCompartimentSearch[] = [];
    compartimenteOriginControl = new FormControl();
    isCompartimenteOriginLoading = false;
    showCompartimenteOriginMessage = true;
    compartimenteOriginLoadComplete = false;

    operatoriOrigin: Generic[] = [];
    filteredOperatoriOrigin: Generic[] = [];
    operatoriOriginControl = new FormControl();

    directiiDestinatie: Generic[] = [];
    filteredDirectiiDestinatie: Generic[] = [];
    directiiDestinatieControl = new FormControl();

    compartimenteDestinatie: GenericCompartimentSearch[] = [];
    compartimenteDestinatieControl = new FormControl();
    isCompartimenteDestinatieLoading = false;
    showCompartimenteDestinatieMessage = true;
    compartimenteDestinatieLoadComplete = false;

    operatoriDestinatie: Generic[] = [];
    filteredOperatoriDestinatie: Generic[] = [];
    operatoriDestinatieControl = new FormControl();

    filtreBorderouActive: boolean = false;
    searchParamsForm!: FormGroup;

    //Form Initial Values
    initialFormValues = {
        origin_directie_id: null,
        origin_compartiment_id: null,
        origin_user_id: -1,
        destinatie_directie_id: null,
        destinatie_compartiment_id: null,
        destinatie_user_id: -1,
        document_number: null,
        date_input_start: '',
        date_input_end: '',
    };

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            origin_directie_id: new FormControl(null),
            origin_compartiment_id: new FormControl(null),
            origin_user_id: new FormControl(-1),
            destinatie_directie_id: new FormControl(null),
            destinatie_compartiment_id: new FormControl(null),
            destinatie_user_id: new FormControl(-1),
            document_number: new FormControl(null),
            date_input_start: new FormControl(''),
            date_input_end: new FormControl(''),
        });
    }

    resetForm() {
        this.resetFieldsSearch();

        this.searchParamsForm.reset(this.initialFormValues);
        this.filtreBorderouActive = false;
        this.rerenderDT();
        this.showCard = true;
    }

    resetFieldsSearch() {
        this.filteredDirectiiOrigin = this.directiiOrigin;
        this.directiiOriginControl.setValue('');

        this.compartimenteOrigin = [];
        this.compartimenteOriginControl.setValue('');

        this.operatoriOrigin = [];
        this.filteredOperatoriOrigin = [];
        this.operatoriOriginControl.setValue('');

        this.filteredDirectiiDestinatie = this.directiiDestinatie;
        this.directiiDestinatieControl.setValue('');

        this.compartimenteDestinatie = [];
        this.compartimenteDestinatieControl.setValue('');

        this.operatoriDestinatie = [];
        this.filteredOperatoriDestinatie = [];
        this.operatoriDestinatieControl.setValue('');

        this.destroy$.next();
        this.destroy$.complete();
        this.renderFieldsSearch()
    }

    applyFilters(): void {
        this.showCard = false;
        this.filtreBorderouActive = true;
        this.rerenderDT(true);
    }

    refresh(): void {
        this.filtreBorderouActive = false;
        this.rerenderDT(false);
    }

    toggleCard() {
        this.showCard = !this.showCard;
    }

    dtLink = environment.interop.basePath + environment.interop.indoc.borderou.getBorderouDT;

    constructor(
        private http: HttpClient,
        private dialog: MatDialog,
        private borderouService: BorderouService,
        private commonService: CommonService,
        private operatoriService: OperatoriService,
        private appFilesService: AppFilesService,
        public permissionsService: PermissionsService,
    ) { }

    ngOnInit(): void {
        this.renderDT();
        this.buildSearchParamsForm();

        this.loadUserAccess();
        this.loadDirectiiActive();

        this.compartimenteOriginControl.valueChanges.pipe(
            debounceTime(600)
        ).subscribe(compartiment => {
            if (typeof compartiment === 'string') {
                this.fetchCompartimenteOrigin(compartiment);
                this.showCompartimenteOriginMessage = true;
                this.compartimenteOriginLoadComplete = false;
            }
        });

        this.compartimenteDestinatieControl.valueChanges.pipe(
            debounceTime(600)
        ).subscribe(compartiment => {
            if (typeof compartiment === 'string') {
                this.fetchCompartimenteDestinatie(compartiment);
                this.showCompartimenteDestinatieMessage = true;
                this.compartimenteDestinatieLoadComplete = false;
            }
        });

        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    loadUserAccess(): void {
        this.operatoriService.userAccess$
            .pipe(takeUntil(this.destroy$))
            .subscribe(userAccesses => {
                this.userAccesses = userAccesses;
                this.activeAccess = this.userAccesses.find(access => access.active) || null;
            });
    }

    renderFieldsSearch() {
        this.directiiOriginControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            debounceTime(150),
            switchMap((inputValue) => {
                if (typeof inputValue !== 'string') {
                    return of(this.directiiOrigin);
                }
                if (inputValue.length < 1) {
                    this.filteredDirectiiOrigin = this.directiiOrigin;
                    return of(this.directiiOrigin);
                }
                return this.filterDirectiiOrigin(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectiiOrigin = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.operatoriOriginControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            debounceTime(150),
            switchMap((inputValue) => {
                if (typeof inputValue !== 'string') {
                    return of(this.operatoriOrigin);
                }
                if (inputValue.length < 1) {
                    this.filteredOperatoriOrigin = this.operatoriOrigin;
                    return of(this.operatoriOrigin);
                }
                return this.filterOperatoriOrigin(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredOperatoriOrigin = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.directiiDestinatieControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            debounceTime(150),
            switchMap((inputValue) => {
                if (typeof inputValue !== 'string') {
                    return of(this.directiiDestinatie);
                }
                if (inputValue.length < 1) {
                    this.filteredDirectiiDestinatie = this.directiiDestinatie;
                    return of(this.directiiDestinatie);
                }
                return this.filterDirectiiDestinatie(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectiiDestinatie = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.operatoriDestinatieControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            debounceTime(150),
            switchMap((inputValue) => {
                if (typeof inputValue !== 'string') {
                    return of(this.operatoriDestinatie);
                }
                if (inputValue.length < 1) {
                    this.filteredOperatoriDestinatie = this.operatoriDestinatie;
                    return of(this.operatoriDestinatie);
                }
                return this.filterOperatoriDestinatie(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredOperatoriDestinatie = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    loadDirectiiActive() {
        this.isFilterLoading = true;
        this.commonService
            .getDirectiiActive()
            .pipe(
                map((results: any) => [...results]),
                finalize(() => this.isFilterLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.directiiOrigin = results;
                    this.directiiDestinatie = results;

                    this.filteredDirectiiOrigin = [...this.directiiOrigin];
                    this.filteredDirectiiDestinatie = [...this.directiiDestinatie];
                },
                error: (error) => {
                    console.error('Error loading directii', error);
                    this.filteredDirectiiOrigin = [];
                    this.filteredDirectiiDestinatie = [];
                },
            });
    }

    onDirectiiOriginChange(selectedCompartiment: Generic): void {
        this.searchParamsForm.controls['origin_directie_id'].patchValue(selectedCompartiment.id);

        this.compartimenteOriginControl.patchValue('');
        this.searchParamsForm.controls['origin_compartiment_id'].patchValue(null);

        this.operatoriOrigin = [];
        this.filteredOperatoriOrigin = [];
        this.operatoriOriginControl.patchValue('');
        this.searchParamsForm.controls['origin_user_id'].patchValue(-1);
    }

    displayDirectiiOrigin(selectedDirectie: Generic): string {
        return selectedDirectie && selectedDirectie.name ? selectedDirectie.name : '';
    }

    onDirectiiOriginBlur() {
        const currentValue = this.directiiOriginControl.value;

        // If the value is a string (not an object), it means user didn't select from list
        if (typeof currentValue === 'string') {
            this.directiiOriginControl.patchValue('');
            this.searchParamsForm.controls['origin_directie_id'].patchValue(null);

            this.compartimenteOriginControl.patchValue('');
            this.searchParamsForm.controls['origin_compartiment_id'].patchValue(null);

            this.operatoriOrigin = [];
            this.filteredOperatoriOrigin = [];
            this.operatoriOriginControl.patchValue('');
            this.searchParamsForm.controls['origin_user_id'].patchValue(-1);
        }
    }

    fetchCompartimenteOrigin(compartiment: string) {
        // const directieValue = this.searchParamsForm.get('origin_directie_id')?.value;
        // const directie = directieValue && directieValue !== -1 ? directieValue : undefined;
        if (compartiment.length >= 2) {
            this.isCompartimenteOriginLoading = true;
            this.commonService.searchCompartimenteActive(undefined, compartiment).subscribe({
                next: (response: any) => {
                    this.compartimenteOrigin = response;
                    this.isCompartimenteOriginLoading = false;
                    this.compartimenteOriginLoadComplete = true;
                    this.showCompartimenteOriginMessage = this.compartimenteOrigin.length === 0;
                },
                error: (err: any) => {
                    this.isCompartimenteOriginLoading = false;
                    this.showCompartimenteOriginMessage = true;
                    this.compartimenteOriginLoadComplete = true;
                    console.log(err);
                }
            });
        } else {
            this.compartimenteOrigin = [];
            this.showCompartimenteOriginMessage = true;
            this.compartimenteOriginLoadComplete = false;
        }
    }

    onCompartimenteOriginSelected(selectedCompartiment: GenericCompartimentSearch): void {
        this.searchParamsForm.controls['origin_compartiment_id'].patchValue(selectedCompartiment.id);

        this.directiiOriginControl.setValue({
            id: selectedCompartiment.directie_id,
            name: selectedCompartiment.directie_name,
        });
        this.searchParamsForm.controls['origin_directie_id'].patchValue(selectedCompartiment.directie_id);

        this.loadOperatoriOrigin();
        this.operatoriOriginControl.patchValue('');
        this.searchParamsForm.controls['origin_user_id'].patchValue(-1);
    }

    displayCompartimenteOrigin(selectedCompartiment: GenericCompartimentSearch): string {
        return selectedCompartiment && selectedCompartiment.name ? selectedCompartiment.name : '';
    }

    onCompartimenteOriginBlur() {
        const currentValue = this.compartimenteOriginControl.value;

        // If the value is a string (not an object), it means user didn't select from list
        if (typeof currentValue === 'string') {
            this.compartimenteOriginControl.patchValue('');
            this.searchParamsForm.controls['origin_compartiment_id'].patchValue(null);

            this.operatoriOrigin = [];
            this.filteredOperatoriOrigin = [];
            this.operatoriOriginControl.patchValue('');
            this.searchParamsForm.controls['origin_user_id'].patchValue(-1);
        }
    }

    loadOperatoriOrigin() {
        this.isFilterLoading = true;
        const payload: UserOrganigramaSearchPayload = {
            directie_id: this.searchParamsForm.controls['origin_directie_id'].value,
            compartiment_id: this.searchParamsForm.controls['origin_compartiment_id'].value,
            view_all: 1,
        }
        this.commonService
            .getUsersByOrganigrama(payload)
            .pipe(
                map((results: any) => [...results]),
                finalize(() => { this.isFilterLoading = false })
            ).subscribe({
                next: (data) => {
                    this.operatoriOrigin = [...data];
                    this.filteredOperatoriOrigin = [...this.operatoriOrigin];
                },
                error: (error) => {
                    console.error(error);
                    this.filteredOperatoriOrigin = [];
                },
            });
    }

    onOperatoriOriginChange(operatorId: number): void { }

    onDirectiiDestinatieChange(selectedCompartiment: Generic): void {
        this.searchParamsForm.controls['destinatie_directie_id'].patchValue(selectedCompartiment.id);

        this.compartimenteDestinatieControl.patchValue('');
        this.searchParamsForm.controls['destinatie_compartiment_id'].patchValue(null);

        this.operatoriDestinatie = [];
        this.filteredOperatoriDestinatie = [];
        this.operatoriDestinatieControl.patchValue('');
        this.searchParamsForm.controls['destinatie_user_id'].patchValue(-1);
    }

    displayDirectiiDestinatie(selectedDirectie: Generic): string {
        return selectedDirectie && selectedDirectie.name ? selectedDirectie.name : '';
    }

    onDirectiiDestinatieBlur() {
        const currentValue = this.directiiDestinatieControl.value;

        // If the value is a string (not an object), it means user didn't select from list
        if (typeof currentValue === 'string') {
            this.directiiDestinatieControl.patchValue('');
            this.searchParamsForm.controls['destinatie_directie_id'].patchValue(null);

            this.compartimenteDestinatieControl.patchValue('');
            this.searchParamsForm.controls['destinatie_compartiment_id'].patchValue(null);

            this.operatoriDestinatie = [];
            this.filteredOperatoriDestinatie = [];
            this.operatoriDestinatieControl.patchValue('');
            this.searchParamsForm.controls['destinatie_user_id'].patchValue(-1);
        }
    }

    fetchCompartimenteDestinatie(compartiment: string) {
        // const directieValue = this.searchParamsForm.get('destinatie_directie_id')?.value;
        // const directie = directieValue && directieValue !== -1 ? directieValue : undefined;
        if (compartiment.length >= 2) {
            this.isCompartimenteDestinatieLoading = true;
            this.commonService.searchCompartimenteActive(undefined, compartiment).subscribe({
                next: (response: any) => {
                    this.compartimenteDestinatie = response;
                    this.isCompartimenteDestinatieLoading = false;
                    this.compartimenteDestinatieLoadComplete = true;
                    this.showCompartimenteDestinatieMessage = this.compartimenteDestinatie.length === 0;
                },
                error: (err: any) => {
                    this.isCompartimenteDestinatieLoading = false;
                    this.showCompartimenteDestinatieMessage = true;
                    this.compartimenteDestinatieLoadComplete = true;
                    console.log(err);
                }
            });
        } else {
            this.compartimenteDestinatie = [];
            this.showCompartimenteDestinatieMessage = true;
            this.compartimenteDestinatieLoadComplete = false;
        }
    }

    onCompartimenteDestinatieSelected(selectedCompartiment: GenericCompartimentSearch): void {
        this.searchParamsForm.controls['destinatie_compartiment_id'].patchValue(selectedCompartiment.id);

        this.directiiDestinatieControl.setValue({
            id: selectedCompartiment.directie_id,
            name: selectedCompartiment.directie_name,
        });
        this.searchParamsForm.controls['destinatie_directie_id'].patchValue(selectedCompartiment.directie_id);

        this.loadOperatoriDestinatie();
        this.operatoriDestinatieControl.patchValue('');
        this.searchParamsForm.controls['destinatie_user_id'].patchValue(-1);
    }

    displayCompartimenteDestinatie(selectedCompartiment: GenericCompartimentSearch): string {
        return selectedCompartiment && selectedCompartiment.name ? selectedCompartiment.name : '';
    }

    onCompartimenteDestinatieBlur() {
        const currentValue = this.compartimenteDestinatieControl.value;

        // If the value is a string (not an object), it means user didn't select from list
        if (typeof currentValue === 'string') {
            this.compartimenteDestinatieControl.patchValue('');
            this.searchParamsForm.controls['destinatie_compartiment_id'].patchValue(null);

            this.operatoriDestinatie = [];
            this.filteredOperatoriDestinatie = [];
            this.operatoriDestinatieControl.patchValue('');
            this.searchParamsForm.controls['destinatie_user_id'].patchValue(-1);
        }
    }

    loadOperatoriDestinatie() {
        this.isFilterLoading = true;
        const payload: UserOrganigramaSearchPayload = {
            directie_id: this.searchParamsForm.controls['destinatie_directie_id'].value,
            compartiment_id: this.searchParamsForm.controls['destinatie_compartiment_id'].value,
            view_all: 1,
        }
        this.commonService
            .getUsersByOrganigrama(payload)
            .pipe(
                map((results: any) => [...results]),
                finalize(() => { this.isFilterLoading = false })
            ).subscribe({
                next: (data) => {
                    this.operatoriDestinatie = [...data];
                    this.filteredOperatoriDestinatie = [...this.operatoriDestinatie];
                },
                error: (error) => {
                    console.error(error);
                    this.filteredOperatoriDestinatie = [];
                },
            });
    }

    onOperatoriDestinatieChange(operatorId: number): void { }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectiiOrigin(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiOrigin.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    filterOperatoriOrigin(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.operatoriOrigin.filter(operator =>
            this.normalizeString(operator.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    filterDirectiiDestinatie(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiDestinatie.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    filterOperatoriDestinatie(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.operatoriDestinatie.filter(operator =>
            this.normalizeString(operator.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    renderDT() {
        this.dtOptions = {
            order: [[0, 'desc']],
            responsive: true,
            serverSide: true,
            searching: false,
            pageLength: 25,

            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;

                const date_input_start = this.searchParamsForm.value.date_input_start;
                const date_input_end = this.searchParamsForm.value.date_input_end;

                Object.assign(dataTablesParameters, this.searchParamsForm.value);
                dataTablesParameters.date_input_start = date_input_start ? formatDate(date_input_start, 'yyyy-MM-dd', 'en-US') : '';
                dataTablesParameters.date_input_end = date_input_end ? formatDate(date_input_end, 'yyyy-MM-dd', 'en-US') : '';
                dataTablesParameters.filtre_borderou_active = this.filtreBorderouActive;


                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;

                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });

                        if (resp.data.recordsFiltered < dataTablesParameters.start) {
                            this.rerenderDT(true);  // sets dataTablesParameters.start = 0, goes to page 1
                        }
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id_lot',
                },
                {
                    title: 'Creator',
                    data: null,
                    sortable: false,
                    render: (data: any, type: any, row: any) => {
                        return `<div><i class="fa fa-user"></i> ${row.creator_user_name || 'n/a'}</div>
                        <div><i class="fa-regular fa-clock"></i> ${row.created || 'n/a'}</div>`
                    }
                },
                {
                    title: 'Nr documente',
                    data: 'number_documents',
                    sortable: false,
                },
                {
                    title: 'Origine',
                    data: null,
                    sortable: false,
                    render: (data: any, type: any, row: any) => {
                        return `<div><i class="fa fa-book"></i> ${row.origin_directie_name || 'n/a'}</div>
                        <div><i class="fa fa-cubes"></i> ${row.origin_compartiment_name || 'n/a'}</div>
                        <div><i class="fa fa-user"></i> ${row.origin_user_name || 'n/a'}</div>`
                    }
                },
                {
                    title: 'Destinație',
                    data: null,
                    sortable: false,
                    render: (data: any, type: any, row: any) => {
                        return `<div><i class="fa fa-book"></i> ${row.destination_directie_name || 'n/a'}</div>
                        <div><i class="fa fa-cubes"></i> ${row.destination_compartiment_name || 'n/a'}</div>
                        <div><i class="fa fa-user"></i> ${row.destination_user_name || 'n/a'}</div>`
                    }
                },
                {
                    title: 'Status',
                    data: null,
                    sortable: false,
                    render: (data: any, type: any, row: any) => {
                        if (row.active) {
                            return `<span class='label label-success'>Activ</span>`
                        } else {
                            return `<span class='label label-danger'>Dezactivat</span>
                            <div><i class="fa fa-user"></i> ${row.cancel_user_name || 'n/a'}</div>
                            <div><i class="fa-regular fa-clock"></i> ${row.cancel_date || 'n/a'}</div>
                            <div><i class="fa-regular fa-comment"></i> ${row.cancel_obs || 'n/a'}</div>
                            `
                        }
                    }
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    orderable: false,
                    className: 'all',
                    render: (data: any, type: any, row: any, met: any) => {
                        return `
                                <div class="d-flex align-items-center gap-1">
                                    <button class="action-btn view" title="Vizualizare documente">
                                        <img class="btn-img" src="../../../../assets/icons/view.svg" alt="view"/>
                                    </button>
                                    <button class="action-btn download" style="font-size: 11px; color: #fff" title="Descarcă borderou">
                                        <i class="btn-img fa fa-download"></i>
                                    </button>   
                                    <button class="action-btn delete" title="Dezactivare borderou">
                                        <img class="btn-img" src="../../../../assets/icons/delete.svg" alt="delete"/>
                                    </button>    
                                </div>
                            `;
                    },
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const viewIcon = lastTd?.querySelector('.view') as HTMLElement;
                const downloadIcon = lastTd?.querySelector('.download') as HTMLElement;
                const deleteIcon = lastTd?.querySelector('.delete') as HTMLElement;

                if (viewIcon) {
                    if (!this.permissionsService.hasPermission('indoc-module.borderouri.view')) {
                        viewIcon.style.display = 'none';
                    }

                    viewIcon.addEventListener('click', () => {
                        const { id_lot } = data;
                        this.openViewDialog(id_lot);
                    });
                }

                if (downloadIcon) {
                    if (!this.permissionsService.hasPermission('indoc-module.borderouri.download') || !data.active) {
                        downloadIcon.style.display = 'none';
                    }

                    downloadIcon.addEventListener('click', () => {
                        const { id_lot } = data;
                        this.downloadBorderou(id_lot);
                    });
                }

                if (deleteIcon) {
                    if (!this.permissionsService.hasPermission('indoc-module.borderouri.delete') || !data.active) {
                        deleteIcon.style.display = 'none';
                    }

                    deleteIcon.addEventListener('click', () => {
                        const { id_lot } = data;
                        this.deleteBorderou(id_lot);
                    });
                }

                return row;
            },
        };
    }

    rerenderDT(paginate: boolean = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(paginate);
        });

        this.showCard = false;
    }

    openAddDialog() {
        const dialogRef = this.dialog.open(AddBorderouComponent, {
            minHeight: '100vh',
            maxHeight: '100vh',
            minWidth: '100vw',
            maxWidth: '100vw',
            disableClose: true,
            data: {
                activeAccess: this.activeAccess
            }
        });

        dialogRef.afterClosed().subscribe(({ success }: { success: boolean }) => {
            if (success) {
                this.rerenderDT();
                Swal.fire({
                    title: helper.dialogConfig.headers.success,
                    text: 'Borderoul înregistrat cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.dialogConfig.buttonLabels.close,
                    allowOutsideClick: false,
                });
            }
        });
    }

    openViewDialog(id: number): void {
        const dialogRef = this.dialog.open(ViewBorderouComponent, {
            minHeight: '100vh',
            maxHeight: '100vh',
            minWidth: '100vw',
            maxWidth: '100vw',
            disableClose: true,
            data: {
                borderouId: id,
            },
        });
        // dialogRef.afterClosed().subscribe();
    }

    downloadBorderou(id: number) {
        let fullPath = this.borderouService.printUrl + id;
        this.isLoading = true;
        this.appFilesService.downloadFile(fullPath)
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                console.error('Print error: ', err);
                this.isLoading = false;
            })
    }

    deleteBorderou(id: number) {
        Swal.fire({
            title: helper.swalTextConfig.commonTitles.areYouSure,
            text: 'Vă rugăm să confirmați procedura de invalidare borderou!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: helper.swalTextConfig.validationMessages.confirmButton,
            cancelButtonText: helper.swalTextConfig.validationMessages.cancelButton,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
            input: 'text', // Enable text input
            inputPlaceholder: 'Motiv de invalidare...',
            inputValidator: (value: any) => {
                if (!value) {
                    return 'Este necesar completarea un motiv de invalidare!';
                }
                return null; // Return null if input is valid
            },
            preConfirm: (inputValue) => {
                return this.borderouService.cancel(id, inputValue)
                    .pipe(finalize(() => (this.isLoading = false)))
                    .toPromise()
                    .then((response) => {
                        return response;
                    })
                    .catch((err: any) => {
                        let msg;
                        try {
                            msg = err.error.errors.message[0];
                        } catch (error) {
                            msg = helper.dialogConfig.generalMessages.error;
                        }
                        Swal.hideLoading();
                        Swal.showValidationMessage(`Request failed: ${msg}`);
                    });
            }
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: this.helper.swalTextConfig.headers.success,
                    text: 'Borderoul a fost invalidat cu succes.',
                    icon: 'success',
                    confirmButtonText: helper.swalTextConfig.buttonLabels.close,
                }).then((result) => {
                    this.rerenderDT();
                });
            }
        });
    }
}
